/**
 * Pure derivations for group runs on the portal (GROUP-ORIENTATION-PLAN
 * Phase 5): the joined display row behind the Cohorts tab's run list and
 * Live Sessions' one-row-per-group view.
 *
 * Statuses are the module's own (`Lobby | Running | Paused | Completed |
 * Cancelled`) — unlike cohort pairings nothing is derived, the run row is
 * authoritative. What IS derived: the joined tally and the per-member
 * connected/check states from `visible_group_run_participants`.
 */

export type GroupRunStatusTag = 'Lobby' | 'Running' | 'Paused' | 'Completed' | 'Cancelled';

export type GroupMemberState = {
  assignmentId: bigint;
  email: string;
  displayName: string;
  connected: boolean;
  pendingChecks: number;
};

/** "x of y joined" per the plan's player-list contract. */
export function joinedTally(members: readonly { connected: boolean }[]): {
  joined: number;
  total: number;
} {
  return {
    joined: members.filter((m) => m.connected).length,
    total: members.length,
  };
}

/** Which controls a run's row offers, per status (plan decisions 9-12). */
export function runControls(status: GroupRunStatusTag): {
  start: boolean;
  pause: boolean;
  resume: boolean;
  handOver: boolean;
  cancel: boolean;
  join: boolean;
} {
  const live = status === 'Lobby' || status === 'Running' || status === 'Paused';
  return {
    start: status === 'Lobby',
    pause: status === 'Running',
    resume: status === 'Paused',
    handOver: live,
    cancel: live,
    // Lobby too: the leader waits in the lobby and presses Start from the
    // client (#168), and an observer may sit in early.
    join: live,
  };
}

/**
 * Is the signed-in user this run's leader? Decides what Join does: a leader
 * launches straight into the session (the module admits them as Participant on
 * connect), an observer first records `join_session_as_observer`. Sending the
 * leader through the observer path would leave a 12-hour Observer grant racing
 * their Participant add on every connect (#168).
 */
export function isRunLeader(leaderEmail: string, currentUserEmail: string): boolean {
  return currentUserEmail !== '' && leaderEmail.toLowerCase() === currentUserEmail.toLowerCase();
}

/**
 * The name of the live run this email LEADS among a pairing's runs, or
 * null. Drives the pairing progress line: a roster leader holds no
 * assignment under the pairing (assign-with-groups, decision 2026-09-18)
 * — or a NotStarted one from the regroup path — and reads
 * "leads <group>" instead of "not started".
 */
export function ledRunName(
  email: string,
  runs: readonly { leaderEmail: string; name: string; statusTag: GroupRunStatusTag }[],
): string | null {
  const n = email.trim().toLowerCase();
  const led = runs.find(
    (r) => r.statusTag !== 'Cancelled' && r.leaderEmail.trim().toLowerCase() === n,
  );
  return led ? led.name : null;
}

/**
 * The lobby's waiting text per mode — the same vocabulary the Unreal
 * client and the agent use, so all three surfaces tell one story.
 */
export function lobbyWaitText(modeTag: string | undefined, leaderName: string | null): string {
  return modeTag === 'Auto'
    ? 'Starting when everyone is here'
    : `Waiting for ${leaderName?.trim() || 'the leader'} to start`;
}

/**
 * The live runs this email LEADS, optionally on one site. Drives the "You
 * lead" section of My Orientations: a leader holds no assignment under the
 * pairing (decision 9), so without this the page is empty for them.
 */
export function runsLedBy<
  R extends { leaderEmail: string; projectId: string; status: { tag: string } },
>(runs: readonly R[], email: string, projectId?: string): R[] {
  return runs.filter(
    (r) =>
      isRunLeader(r.leaderEmail, email) &&
      r.status.tag !== 'Completed' &&
      r.status.tag !== 'Cancelled' &&
      (!projectId || r.projectId === projectId),
  );
}
